import React, { useContext } from "react"
import styled, { css } from "styled-components"
import { ContentfulParagraph, ParagraphWithRemarkFragment } from "../../types/graphql-types"
import { PriceContext, PriceContextType } from "../context/priceContext"
import colors from "../utils/colors"
import { generateAllPriceWithCurrency } from "../utils/currencyList"
import clsx from "clsx"

export interface Props extends StylingProps, React.HTMLAttributes<HTMLHeadingElement> {
  content?: ParagraphWithRemarkFragment | ContentfulParagraph | string | null
  level: HeadingLevel
}

interface StylingProps {
  size?: HeadingLevel
  isInverted?: boolean
  pro?: boolean
  ignoreMarginBottom?: boolean
}

const headingStyles = css`
  color: ${colors.dark};
`
const styledHeading = {
  h1: styled.h1`
    ${headingStyles}
  `,
  h2: styled.h2`
    ${headingStyles}
  `,
  h3: styled.h3`
    ${headingStyles}
  `,
  h4: styled.h4`
    ${headingStyles}
  `,
  h5: styled.h5`
    ${headingStyles}
  `,
}

type HeadingLevel = keyof typeof styledHeading

const Heading = ({
  content,
  level,
  size,
  isInverted,
  ignoreMarginBottom = false,
  pro = false,
  ...rest
}: Props) => {
  if (!content) {
    return null
  }
  const { className: c, ...attr } = rest
  const className = clsx(
    c,
    size,
    {
      inverted: isInverted,
      "ignore-margin-bottom": ignoreMarginBottom,
    },
    pro && "heading--pro"
  )
  const H: HeadingLevel = level
  const StyledH = styledHeading[H]
  const { priceData } = useContext(PriceContext) as PriceContextType

  const html =
    typeof content === "string"
      ? content
          .replace(/\\n/g, "<br/>")
          .replace(/\*[^\*]*\*/g, em => `<em>${em.substring(1, em.length - 1)}</em>`)
      : (content as ParagraphWithRemarkFragment).paragraph?.childMarkdownRemark?.html

  if (html) {
    const contentWithDynamicPrice = generateAllPriceWithCurrency(
      priceData.currency,
      priceData,
      priceData.currencySymbol,
      html
    )

    return (
      <StyledH
        {...attr}
        className={className}
        dangerouslySetInnerHTML={{
          __html: contentWithDynamicPrice,
        }}
      />
    )
  } else {
    return null
  }
}

export default Heading
